import React from 'react'
import { useNavigate } from "react-router-dom"

import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import SendIcon from '@mui/icons-material/ThumbUpAltRounded'

import PageLogo from '../layout/hgqn/PageLogo'
import './Activation.scss'

import API from '../api/fetchAPI'

export default function FirstTimeUserSetup() {
    const navigate = useNavigate()
    
    const [username, setUsername] = React.useState('')
    const [user, setUser] = React.useState(null)
    const [loading, setLoading] = React.useState(false)
    const [error, setError] = React.useState('')
    
    const [password, setPassword] = React.useState('')
    const [passwordConfirm, setPasswordConfirm] = React.useState('')
    const [firstname, setFirstname] = React.useState('')
    const [lastname, setLastname] = React.useState('')
    
    const [passwordCheckInfo, setPasswordCheckInfo] = React.useState({
        total: false,
        upper: false,
        lower: false,
        digit: false,
        special: false
    })
    
    const [passwordCheck, setPasswordCheck] = React.useState(false)
    const [passwordsMatch, setPasswordsMatch] = React.useState(false)
    const [messageState, setMessageState] = React.useState('')
    const [message, setMessage] = React.useState('')
    
    const upperTest = /[ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÜ]/
    const lowerTest = /[abcdefghijklmnopqrstuvwxyzäöü]/
    const digitTest = /[0123456789]/
    const specialTest = /[ `°!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/
    
    const checkPassword = () => {
        if(!password) {
            return
        }
        
        let check = {
            total: password.length >= 8,
            upper: upperTest.test(password),
            lower: lowerTest.test(password),
            digit: digitTest.test(password),
            special: specialTest.test(password)
        }
        
        setPasswordCheckInfo(check)
        setPasswordCheck(check.total && check.upper && check.lower && check.digit && check.special)
    }
    
    const matchPasswords = () => {
        setPasswordsMatch(password === passwordConfirm)
    }
    
    React.useEffect(() => {
        checkPassword()
    }, [password])
    
    React.useEffect(() => {
        matchPasswords()
    }, [password, passwordConfirm])
    
    React.useEffect(() => {
        if(user) {
            if(user.firstname) setFirstname(user.firstname)
            if(user.lastname) setLastname(user.lastname)
        }
    }, [user])
    
    const handleUsernameChange = (event) => setUsername(event.target.value)
    const handlePasswordChange = (event) => setPassword(event.target.value)
    const handlePasswordConfirmChange = (event) => setPasswordConfirm(event.target.value)
    const handleFirstnameChange = (event) => setFirstname(event.target.value)
    const handleLastnameChange = (event) => setLastname(event.target.value)
    
    const getConstraintClass = (key) => {
        if(typeof password === 'string' && password.length > 0) {
            return passwordCheckInfo[key] ? 'success' : 'error'   
        } else {
            return null
        }
    }
    
    const lookupUser = () => {
        if(!username || username.trim().length === 0) {
            setError('Please enter your username or email')
            return
        }
        
        setLoading(true)
        setError('')
        
        API.post('/api/user/lookup-inactive', {
            params: {
                username: username.trim()
            },
            doNotThrowFor: [404]
        }).then(foundUser => {
            if(foundUser && foundUser._id) {
                setUser(foundUser)
            } else {
                setError('No inactive account found for this username/email')
            }
        }).catch(err => {
            setError('Error looking up user')
        }).finally(() => {
            setLoading(false)
        })
    }
    
    const sendRegistration = () => {
        if(typeof firstname !== 'string' || firstname.trim().length <= 0) {
            setMessageState('error')
            setMessage('The field "firstname" is required')
            return
        }
        if(typeof lastname !== 'string' || lastname.trim().length <= 0) {
            setMessageState('error')
            setMessage('The field "lastname" is required')
            return
        }
        
        setMessage('')
        
        API.post('/api/user/first-time-setup', {
            params: {
                id: user._id,
                password: password,
                passwordConfirm: passwordConfirm,
                firstname: firstname,
                lastname: lastname
            }
        }).then(user => {
            setMessageState('success')
            setMessage('Account setup completed! Redirecting to login...')
            setTimeout(() => {
                navigate('/')
            }, 2000)
        }).catch(err => {
            setMessageState('error')
            setMessage('Error setting up account')
        })
    }
    
    const goBack = () => {
        if(user) {
            setUser(null)
            setUsername('')
            setPassword('')
            setPasswordConfirm('')
            setFirstname('')
            setLastname('')
        } else {
            navigate('/')
        }
    }
    
    return (
        <div className="activation-page">
            <PageLogo/>
            
            {!user ? (
                <div className="activation-form">
                    <h1>First Time User Setup</h1>
                    <p style={{marginBottom: '20px'}}>
                        If you have received an account but haven't set it up yet, enter your username or email below.
                    </p>
                    
                    <TextField
                        id="username"
                        label="Username or Email"
                        variant="filled"
                        size="small"
                        value={username}
                        onChange={handleUsernameChange}
                        onKeyDown={(e) => e.key === 'Enter' && lookupUser()}
                    />
                    
                    {error && (
                        <div className="message error" style={{marginTop: '10px'}}>
                            {error}
                        </div>
                    )}
                    
                    <Button
                        style={{ alignSelf: 'center', marginTop: '20px' }}
                        onClick={lookupUser}
                        disabled={loading || !username.trim()}
                    >
                        {loading ? 'Looking up...' : 'Continue'}
                    </Button>
                    
                    <Button
                        style={{ alignSelf: 'center', marginTop: '10px' }}
                        variant="text"
                        onClick={goBack}
                    >
                        Back to Login
                    </Button>
                </div>
            ) : (
                <div className="activation-form">
                    <h1>Complete Your Account Setup</h1>
                    
                    <TextField
                        disabled
                        id="id"
                        label="User ID"
                        variant="filled"
                        size="small"
                        value={user._id}
                    />
                    
                    <TextField
                        disabled
                        id="username"
                        label="Username"
                        variant="filled"
                        size="small"
                        value={user.username}
                    />
                    
                    <TextField
                        disabled
                        id="email"
                        label="Email"
                        variant="filled"
                        size="small"
                        value={user.email}
                    />
                    
                    <div className="spacer"></div>
                    
                    <TextField
                        type={'password'}
                        id="password"
                        label="Password"
                        variant="filled"
                        size="small"
                        value={password}
                        onChange={handlePasswordChange}
                    />
                    
                    <div className="password-format-message">
                        The password must have at least<br/>
                        <ul>
                            <li className={getConstraintClass('total')}>8 characters in total</li>
                            <li className={getConstraintClass('upper')}>1 uppercase character</li>
                            <li className={getConstraintClass('lower')}>1 lowercase character</li>
                            <li className={getConstraintClass('digit')}>1 digit</li>
                            <li className={getConstraintClass('special')}>1 special character</li>
                        </ul>
                    </div>
                    
                    <TextField
                        type={'password'}
                        id="password_confirm"
                        label="Repeat Password"
                        variant="filled"
                        size="small"
                        value={passwordConfirm}
                        onChange={handlePasswordConfirmChange}
                    />
                    
                    <div className={`password-match-message ${typeof passwordConfirm === 'string' && passwordConfirm.length > 0 ? 'show' : null} ${passwordsMatch ? 'success' : 'error'}`}>
                        <span className="error">Passwords do not match!</span>
                        <span className="success">Passwords match!</span>
                    </div>
                    
                    <div className="spacer"></div>
                    
                    <TextField
                        id="firstname"
                        label="Firstname"
                        variant="filled"
                        size="small"
                        value={firstname}
                        onChange={handleFirstnameChange}
                    />
                    
                    <TextField
                        id="lastname"
                        label="Lastname"
                        variant="filled"
                        size="small"
                        value={lastname}
                        onChange={handleLastnameChange}
                    />
                    
                    {typeof message === 'string' && message.length > 0 ? 
                        <div className={`message ${messageState === 'error' ? 'error' : 'success'}`}>
                            {message}
                        </div> 
                    : null}
                    
                    <div className="spacer"></div>
                    
                    <Button
                        style={{ alignSelf: 'center' }}
                        onClick={sendRegistration}
                        endIcon={<SendIcon/>}
                        disabled={passwordCheck == false || passwordsMatch == false}
                    >
                        Complete Setup
                    </Button>
                    
                    <Button
                        style={{ alignSelf: 'center', marginTop: '10px' }}
                        variant="text"
                        onClick={goBack}
                    >
                        Back
                    </Button>
                </div>
            )}
        </div>
    )
}